import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Form, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { registerClientPublic, getBusinessDetailsPublic } from '../services/api'; // I'll create getBusinessDetailsPublic

const ReadOnlyInput = ({ value, ...rest }) => (
    <Form.Control {...rest} value={value} readOnly />
);

const ClientSelfRegisterPage = () => {
    const { businessSlug } = useParams();
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: '',
        phone_country_code: '',
        phone: '',
        address: '',
        email: '',
        consent: false,
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [businessLogo, setBusinessLogo] = useState(''); // Placeholder for logo

    // Fetch business details based on slug
    useEffect(() => {
        const fetchBusinessDetails = async () => {
            try {
                setLoading(true);
                // I'll need a public endpoint to get business details by slug
                const response = await getBusinessDetailsPublic(businessSlug);
                setBusinessName(response.data.name);
                setBusinessLogo(response.data.logo_url || ''); // Use logo_url from API
            } catch (err) {
                setError(t('clientSelfRegisterPage.error.fetchBusinessDetails'));
                toast.error(t('clientSelfRegisterPage.error.fetchBusinessDetails'));
            } finally {
                setLoading(false);
            }
        };

        fetchBusinessDetails();
    }, [businessSlug, t]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handlePhoneCountryCodeChange = (value) => {
        setFormData(prev => ({ ...prev, phone_country_code: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name || !formData.phone || !formData.consent) {
            setError(t('clientSelfRegisterPage.error.requiredFields'));
            return;
        }
        if (!formData.consent) {
            setError(t('clientSelfRegisterPage.error.consentRequired'));
            return;
        }

        setSubmitting(true);
        const toastId = toast.loading(t('clientSelfRegisterPage.toasts.registering'));
        try {
            await registerClientPublic(businessSlug, formData);
            toast.success(t('clientSelfRegisterPage.toasts.registrationSuccess'), { id: toastId });
            // Optionally redirect or show a success message
            setFormData({
                name: '',
                phone_country_code: '',
                phone: '',
                address: '',
                email: '',
                consent: false,
            }); // Clear form
        } catch (err) {
            const errorMessage = err.response?.data?.message || t('clientSelfRegisterPage.error.registrationFailed');
            setError(errorMessage);
            toast.error(errorMessage, { id: toastId });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('loading')}</span>
                </Spinner>
            </Container>
        );
    }

    if (error && !businessName) { // If there's an error and no business name was fetched, something went wrong with the slug
        return (
            <Container className="mt-5 text-center">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <div className="text-center mb-4">
                {businessLogo && (
                    <img
                        src={businessLogo}
                        alt={`${businessName} Logo`}
                        style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '4px solid #eee',
                            marginBottom: '20px'
                        }}
                    />
                )}
                <h2>{t('clientSelfRegisterPage.title', { businessName })}</h2>
                <p>{t('clientSelfRegisterPage.subtitle', { businessName })}</p>
            </div>

            <Row className="justify-content-md-center">
                <Col md={6}>
                    <Form onSubmit={handleSubmit}>
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form.Group className="mb-3">
                            <Form.Label>{t('clientSelfRegisterPage.form.nameLabel')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('clientSelfRegisterPage.form.phoneLabel')}</Form.Label>
                            <Row>
                                <Col xs={4} md={3}>
                                    <PhoneInput
                                        value={formData.phone_country_code}
                                        onChange={handlePhoneCountryCodeChange}
                                        numberInputComponent={ReadOnlyInput}
                                        className="phone-input-custom"
                                        defaultCountry="CO"
                                        international
                                    />
                                </Col>
                                <Col xs={8} md={9}>
                                    <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('clientSelfRegisterPage.form.phonePlaceholder')} />
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('clientSelfRegisterPage.form.emailLabel')}</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('clientSelfRegisterPage.form.addressLabel')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="consent"
                                label={t('clientSelfRegisterPage.form.consentLabel', { businessName })}
                                checked={formData.consent}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner animation="border" size="sm" /> : t('clientSelfRegisterPage.form.registerButton')}
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default ClientSelfRegisterPage;
